/**
 * Phase 2.5: Dictation Session Hook
 * 
 * Core state management for the operative dictation experience.
 * Handles speech recognition, structured note parsing, and session lifecycle.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type {
  DictationSession,
  DictationStep,
  ProcedureConfirmation,
  ConfirmationStatus,
  DocumentationCompleteness,
  StructuredOpNote,
} from '@/lib/dictation/types';
import {
  EMPTY_STRUCTURED_NOTE,
  EMPTY_COMPLETENESS,
  DEMO_DICTATION_TRANSCRIPT,
} from '@/lib/dictation/types';
import { parseTranscriptToStructuredNote } from '@/lib/dictation/structuredNoteParser';
import { detectDocumentationCompleteness } from '@/lib/dictation/documentationDetector';
import { toast } from 'sonner';

interface UseDictationOptions {
  authorizationId: string | null;
  caseNumber: string;
  surgeonId: string;
  authorizedCpts: Array<{ code: string; description: string }>;
  isDemoMode: boolean;
}

export function useDictation({
  authorizationId,
  caseNumber,
  surgeonId,
  authorizedCpts,
  isDemoMode,
}: UseDictationOptions) {
  const [step, setStep] = useState<DictationStep>('dictating');
  const [rawTranscript, setRawTranscript] = useState('');
  const [liveFragment, setLiveFragment] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [structuredNote, setStructuredNote] = useState<StructuredOpNote>(EMPTY_STRUCTURED_NOTE);
  const [completeness, setCompleteness] = useState<DocumentationCompleteness>(EMPTY_COMPLETENESS);
  const [confirmations, setConfirmations] = useState<ProcedureConfirmation[]>([]);
  const [attested, setAttested] = useState(false);
  const [attestedAt, setAttestedAt] = useState<string | null>(null);
  const [startedAt] = useState(new Date().toISOString());
  const [isSigned, setIsSigned] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  // Audio analysis
  const [audioLevel, setAudioLevel] = useState(0);
  const recognitionRef = useRef<any>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intentRecordingRef = useRef(false);

  // Full transcript = committed + live
  const fullTranscript = useMemo(
    () => (rawTranscript + (liveFragment ? ' ' + liveFragment : '')).trim(),
    [rawTranscript, liveFragment]
  );

  // Auto-update structured note and completeness when transcript changes
  useEffect(() => {
    const parsed = parseTranscriptToStructuredNote(fullTranscript);
    setStructuredNote(parsed);
    const detected = detectDocumentationCompleteness(fullTranscript);
    setCompleteness(detected);
  }, [fullTranscript]);

  // Initialize procedure confirmations from authorized CPTs
  useEffect(() => {
    if (authorizedCpts.length > 0 && confirmations.length === 0) {
      setConfirmations(
        authorizedCpts.map(cpt => ({
          cptCode: cpt.code,
          description: cpt.description,
          status: 'pending' as ConfirmationStatus,
        }))
      );
    }
  }, [authorizedCpts, confirmations.length]);

  const stopAudioCapture = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  const startAudioCapture = useCallback(async () => {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    const getUserMedia = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);
    if (!AudioContextCtor || !getUserMedia) return;

    try {
      const stream = await getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new AudioContextCtor();
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const pollLevel = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setAudioLevel(avg / 255);
        animFrameRef.current = requestAnimationFrame(pollLevel);
      };
      pollLevel();
    } catch {
      stopAudioCapture();
    }
  }, [stopAudioCapture]);

  const stopRecording = useCallback(() => {
    intentRecordingRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // noop
      }
      recognitionRef.current = null;
    }
    stopAudioCapture();
    setIsRecording(false);
    // Commit any remaining live fragment
    if (liveFragment) {
      setRawTranscript(prev => (prev + ' ' + liveFragment).trim());
      setLiveFragment('');
    }
  }, [liveFragment, stopAudioCapture]);

  // Speech recognition using Web Speech API
  const startRecording = useCallback(async () => {
    if (isSigned || isRecording) return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      const message = 'This browser does not support speech recognition. Use Chrome or Safari with microphone permission enabled.';
      setRecordingError(message);
      toast.error('Microphone capture unavailable', { description: message });
      return;
    }

    try {
      setRecordingError(null);
      stopAudioCapture();

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        let interim = '';
        let final = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript + ' ';
          } else {
            interim += transcript;
          }
        }
        if (final) {
          setRawTranscript(prev => (prev + ' ' + final).trim());
        }
        setLiveFragment(interim);
        setRecordingError(null);
      };

      recognition.onerror = (event: any) => {
        if (event.error === 'no-speech' || event.error === 'aborted') return;
        const message = `Speech capture failed (${event.error}). Tap the mic to resume.`;
        setRecordingError(message);
        toast.error('Microphone error', { description: message });
        stopRecording();
      };

      recognition.onend = () => {
        // Auto-restart if still recording
        if (intentRecordingRef.current && recognitionRef.current) {
          try {
            recognitionRef.current.start();
          } catch {
            // Ignore if already started
          }
        }
      };

      intentRecordingRef.current = true;
      recognitionRef.current = recognition;
      recognition.start();
      setIsRecording(true);
      void startAudioCapture();
    } catch (err) {
      stopAudioCapture();
      intentRecordingRef.current = false;
      setIsRecording(false);
      const message = 'Microphone access was blocked. Allow microphone access in the browser, then tap the mic again.';
      setRecordingError(message);
      toast.error('Microphone permission needed', { description: message });
    }
  }, [isSigned, isRecording, startAudioCapture, stopAudioCapture, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      intentRecordingRef.current = false;
      if (recognitionRef.current) recognitionRef.current.stop();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
    };
  }, []);

  // Navigation
  const goToReview = useCallback(() => {
    stopRecording();
    setStep('reviewing');
  }, [stopRecording]);

  const goToConfirmation = useCallback(() => {
    setStep('confirming');
  }, []);

  const goToAttestation = useCallback(() => {
    setStep('attesting');
  }, []);

  const goBackToDictation = useCallback(() => {
    setStep('dictating');
  }, []);

  const goBackToReview = useCallback(() => {
    setStep('reviewing');
  }, []);

  // Procedure confirmation
  const updateConfirmation = useCallback((cptCode: string, status: ConfirmationStatus) => {
    setConfirmations(prev =>
      prev.map(c =>
        c.cptCode === cptCode
          ? { ...c, status, confirmedAt: new Date().toISOString() }
          : c
      )
    );
  }, []);

  // Sign and lock
  const signNote = useCallback(() => {
    if (!attested) return false;
    setAttestedAt(new Date().toISOString());
    setIsSigned(true);
    return true;
  }, [attested]);

  // Demo mode: preload transcript
  const loadDemoTranscript = useCallback(() => {
    setRawTranscript(DEMO_DICTATION_TRANSCRIPT);
    setLiveFragment('');
  }, []);

  // Demo mode: auto-confirm all procedures
  const autoConfirmAll = useCallback(() => {
    setConfirmations(prev =>
      prev.map(c => ({
        ...c,
        status: 'confirmed_performed' as ConfirmationStatus,
        confirmedAt: new Date().toISOString(),
      }))
    );
  }, []);

  const allConfirmed = useMemo(
    () => confirmations.length > 0 && confirmations.every(c => c.status !== 'pending'),
    [confirmations]
  );

  return {
    // Step
    step,
    setStep,
    // Transcript
    rawTranscript,
    liveFragment,
    fullTranscript,
    setRawTranscript,
    // Recording
    isRecording,
    startRecording,
    stopRecording,
    audioLevel,
    recordingError,
    // Structured
    structuredNote,
    completeness,
    // Confirmations
    confirmations,
    updateConfirmation,
    allConfirmed,
    // Attestation
    attested,
    setAttested,
    attestedAt,
    isSigned,
    signNote,
    // Navigation
    goToReview,
    goToConfirmation,
    goToAttestation,
    goBackToDictation,
    goBackToReview,
    // Session meta
    startedAt,
    // Demo
    loadDemoTranscript,
    autoConfirmAll,
  };
}
